import React, { useState } from 'react'
import { AnchorsOfForms } from '@constants/forms'
import { AdvCake } from '@features/advcakeModule'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
import { SimplePersonForm } from '@shared/components/forms/shared/simple-person-form'
import { Image } from '@shared/components/image'
import { Modal } from '@shared/components/modal'
import { Button, ButtonTelegram } from '@shared/componentsStyled/Button'
import { Link } from '@shared/componentsStyled/Link'
import { getMarketingData } from '@shared/pipes/marketing-data'
import { yaCounter } from '@shared/pipes/ya-counter'
import moment from 'moment'
import styled from 'styled-components'

const logInvestLead = (): void => yaCounter.utils.reachGoal('invest_lead')
const logInvestTel = (): void => yaCounter.utils.reachGoal('invest_telegram')

interface ModalSavingsProps {
	showModal: boolean
	onModalClose: () => void
}

export const ModalSavings = ({ showModal, onModalClose }: ModalSavingsProps) => {
	const [successSent, setSuccessSent] = useState(false)

	const handleCloseModal = () => {
		onModalClose()
		setSuccessSent(false)
	}

	return (
		<Modal show={showModal} onClose={handleCloseModal} size='large'>
			{successSent ? (
				<Modal.Body>
					<Modal.Header>
						<ModalTitle>Заявка принята!</ModalTitle>
					</Modal.Header>
					<ModalContent>
						<div>
							<ModalText>
								В ближайшее время с вами свяжется наш менеджер Екатерина Бацунова, чтобы ответить на
								все вопросы.
							</ModalText>
							<ModalSuccessText>
								А пока вы можете вступить в наш открытый чат для инвесторов в Telegram и почитать
								реальные отзывы и обсуждения продукта.
							</ModalSuccessText>
							<ButtonTelegram
								href='https://t.me/invest_creditclub'
								text='Вступить в чат инвесторов'
								onClick={logInvestTel}
							/>
						</div>
						<ModalImage>
							<Image name='savings-modal' />
						</ModalImage>
					</ModalContent>
				</Modal.Body>
			) : (
				<Modal.Body>
					<Modal.Header>
						<ModalTitle>Узнайте всё о Сбережениях</ModalTitle>
					</Modal.Header>
					<ModalContent>
						<div>
							<SimplePersonForm
								email={false}
								onSubmit={async (formProps) => {
									const phone = formProps.phone.replace(/[^0-9]/g, '').slice(-10)
									const advCakeId = `${phone}-${moment(new Date()).format('DD.MM.YYYY-HH:mm:ss')}`
									const payload = {
										product: 'SAVING',
										name: formProps.name.trim(),
										phone,
										marketingData: {
											...getMarketingData(),
											channel: 'Узнать больше',
											advCakeId,
										},
									}
									logInvestLead()
									try {
										await httpService.post(apiUrls.investmentLeads(), payload).then(() => {
											setSuccessSent(true)
										})

										AdvCake.pushEvent({
											pageType: 6,
											user: {
												email: '', // email в виде хеша или уникальный идентификатор, данное поле можно оставить пустым
												type: '', // old, new - опционально, передавать пустое значение в кавычках (''), если тип пользователя неизвестен
											},
											leadInfo: {
												id: advCakeId, //Передаем поле **ID** созданного Лида
												leadid: '', // ID продукта(равно id из пункта выше), если такой есть, если нет, оставляем пустым
												name: 'Сбережения', //наименование услуги лида
												totalPrice: 0, //Константа. Всегда равно 0
												coupon: '', // Промокод. Если механики промокодов нет - пустое поле
											},
										})
									} catch (e: any) {
										throw new Error(e)
									}
								}}
								formId={AnchorsOfForms.investorPopup}
								buttonText={(isSubmitted) =>
									isSubmitted ? 'Отправлено!' : 'Получить консультацию'
								}
								renderControls={({ submitButtonText, disableFields }) => (
									<ModalButton type='submit' disabled={disableFields}>
										{submitButtonText}
									</ModalButton>
								)}
								placeholders={['Как к вам обращаться', 'Номер телефона']}
								simpleInputs
							/>
						</div>
						<div>
							<ModalInfoTitle>Как получить консультацию</ModalInfoTitle>
							<ModalText>
								Оставьте заявку на консультацию или напишите менеджеру в{' '}
								<Link href='https://wa.me/79030785001' target='_blank' rel='noopener noreferrer nofollow'>
									What’s App
								</Link>{' '}
								или{' '}
								<Link
									href='https://t.me/Ekaterinabatsunova'
									target='_blank'
									rel='noopener noreferrer nofollow'
								>
									Telegram
								</Link>
								, чтобы узнать об актуальных условиях, надёжности, регулярности выплат и о том как
								мы защищаем деньги наших клиентов от инфляции.
							</ModalText>
						</div>
					</ModalContent>
				</Modal.Body>
			)}
		</Modal>
	)
}

const ModalContent = styled.div(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr',
	gridGap: 40,
	marginTop: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		gridTemplateColumns: '1fr 1fr',
		gridGap: 80,
	},
}))

const ModalButton = styled(Button)(({ theme }) => ({
	...theme.typography.text16_22,
	marginTop: 10,
	'white-space': 'nowrap',
	height: 40,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		height: 'auto',
		padding: '16px 30px',
	},
}))

const ModalTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	[theme.mediaQueries.mediaMinWidthTablet]: {
		...theme.typography.header56_60,
	},
}))

const ModalInfoTitle = styled.p(({ theme }) => ({
	...theme.typography.header24_32,
	fontWeight: 700,
	marginBottom: 8,
}))

const ModalText = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	width: 'auto',
	[theme.mediaQueries.mediaMinWidthTablet]: {
		width: 420,
	},
}))

const ModalSuccessText = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	width: 'auto',
	margin: '15px 0',
	[theme.mediaQueries.mediaMinWidthTablet]: {
		width: 420,
		margin: '30px 0',
	},
}))

const ModalImage = styled.p(({ theme }) => ({
	display: 'none',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		display: 'block',
	},
}))
